import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  Metric,
  Panel,
  RadioGroup,
  SegmentedControl,
  Toggle,
  cn,
  formatDate,
  formatDateTime,
  formatPercentage,
  useToast,
  type BadgeTone,
} from 'escrowbuild-ui';

import {
  useFixtureVocabulary,
  useMilestones,
  useNotifications,
  usePublications,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import { findProject, type NotificationItem } from '@/data/domain';

const CATEGORIES: NotificationItem['category'][] = ['info', 'success', 'warning', 'error'];

const CATEGORY_TONE: Record<NotificationItem['category'], BadgeTone> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

/** The dot is the same tone as the chip, so a row scans without reading the label. */
const CATEGORY_DOT: Record<NotificationItem['category'], string> = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
};

/** The word each code is shown as. The code itself is a wire value. */
const CATEGORY_LABEL: Record<NotificationItem['category'], AppTranslationKey> = {
  info: 'notifications.category.info',
  success: 'notifications.category.success',
  warning: 'notifications.category.warning',
  error: 'notifications.category.error',
};

const LABEL = 'font-display text-2xs font-semibold uppercase tracking-label text-muted';

/** The default drift that warrants an active notification, as a manager sets it. */
const VARIANCE_THRESHOLD = 5;

/*
  The event list and the channel list are KEYS, resolved by the component.

  Both are module-level constants read from a `<Toggle>` and a `<RadioGroup>`,
  and one of the labels carries a formatted threshold — a formatter reads the
  active locale, so a label built here would be built in whatever language the
  module loaded in and would never follow the header's selector.

  `phraseKey` is the channel as it reads INSIDE a sentence ("delivered by
  email"), which is a different word from the one on the radio button: English
  reaches it by lower-casing the label, which is not something a translation can
  rely on.
*/
const EVENTS: { key: string; labelKey: AppTranslationKey; hintKey: AppTranslationKey }[] = [
  {
    key: 'milestone',
    labelKey: 'notifications.event.milestone',
    hintKey: 'notifications.event.milestoneHint',
  },
  {
    key: 'variance',
    labelKey: 'notifications.event.variance',
    hintKey: 'notifications.event.varianceHint',
  },
  {
    key: 'release',
    labelKey: 'notifications.event.release',
    hintKey: 'notifications.event.releaseHint',
  },
  {
    key: 'call',
    labelKey: 'notifications.event.call',
    hintKey: 'notifications.event.callHint',
  },
];

const CHANNELS: {
  value: string;
  labelKey: AppTranslationKey;
  descriptionKey: AppTranslationKey;
  phraseKey: AppTranslationKey;
}[] = [
  {
    value: 'email',
    labelKey: 'notifications.channel.email',
    descriptionKey: 'notifications.channel.emailHint',
    phraseKey: 'notifications.channel.emailPhrase',
  },
  {
    value: 'in-app',
    labelKey: 'notifications.channel.inApp',
    descriptionKey: 'notifications.channel.inAppHint',
    phraseKey: 'notifications.channel.inAppPhrase',
  },
  {
    value: 'digest',
    labelKey: 'notifications.channel.digest',
    descriptionKey: 'notifications.channel.digestHint',
    phraseKey: 'notifications.channel.digestPhrase',
  },
];

/**
 * The notification history and its selectivity (§13).
 *
 * Two questions share this screen because they are the same question: what was
 * the office told, and what was the investor told. The second half is the one
 * the document cares about — a notification is a decision, not an automatic
 * consequence of a record changing state.
 */
export function NotificationsPage() {
  const { toast } = useToast();
  const t = useT();

  /*
     The three lists this screen reads, in the reader's language. The counts they
     feed are language-independent, so one resolved copy serves both the
     arithmetic and the sentences — there is no second list to keep in step.
  */
  const notifications = useNotifications();
  const milestones = useMilestones();
  const publications = usePublications();
  const vocabulary = useFixtureVocabulary();

  const [read, setRead] = useState<ReadonlySet<string>>(
    () => new Set(notifications.filter((entry) => entry.read).map((entry) => entry.id)),
  );
  const [category, setCategory] = useState('all');
  const [channel, setChannel] = useState('email');
  const [events, setEvents] = useState<Record<string, boolean>>({
    milestone: true,
    variance: true,
    release: true,
    call: false,
  });

  // The unit comes from the formatter, so the label reads correctly in a locale
  // that puts the sign first — and the formatter reads the active locale, which
  // is why the threshold is resolved here rather than at module scope.
  const threshold = formatPercentage(VARIANCE_THRESHOLD, { precision: 0 });

  const channelOptions = useMemo(
    () =>
      CHANNELS.map((entry) => ({
        value: entry.value,
        label: t(entry.labelKey),
        description: t(entry.descriptionKey),
      })),
    [t],
  );

  /** The channel as it reads inside a sentence, which the radio label is not. */
  const channelPhrase = (value: string) => {
    const entry = CHANNELS.find((option) => option.value === value);
    return entry ? t(entry.phraseKey) : value;
  };

  const isRead = (entry: NotificationItem) => entry.read || read.has(entry.id);
  const unread = notifications.filter((entry) => !isRead(entry));

  const visible = [...notifications]
    .filter((entry) => category === 'all' || entry.category === category)
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  const notified = milestones.filter((milestone) => milestone.notified);
  const reachedQuietly = milestones.filter(
    (milestone) => milestone.status === 'reached' && !milestone.notified,
  );
  const quietReleases = publications.filter(
    (publication) => publication.status === 'published' && !publication.notify,
  );

  function markRead(entry: NotificationItem) {
    setRead((current) => new Set(current).add(entry.id));
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('notifications.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('notifications.intro')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t('notifications.logMetric')} value={notifications.length} format="number" />
        <Metric
          label={t('notifications.unreadMetric')}
          value={unread.length}
          format="number"
          tone={unread.length > 0 ? 'info' : 'success'}
          hint={
            unread.length === 0 ? t('notifications.unreadNone') : t('notifications.unreadSome')
          }
        />
        <Metric
          label={t('notifications.milestonesMetric')}
          value={notified.length}
          format="number"
          tone="success"
          hint={t('notifications.milestonesHint', {
            total: milestones.filter((milestone) => milestone.status === 'reached').length,
          })}
        />
        <Metric
          label={t('notifications.quietMetric')}
          value={quietReleases.length}
          format="number"
          tone="neutral"
          hint={t('notifications.quietHint')}
        />
      </div>

      <section className="rounded-xs border border-line bg-surface">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line-subtle px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
              {t('notifications.history')}
            </h2>
            <p className="mt-1 text-sm text-muted">{t('notifications.historyHint')}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={unread.length === 0}
            onClick={() => {
              setRead(new Set(notifications.map((entry) => entry.id)));
              toast({
                variant: 'success',
                title: t('notifications.markedTitle'),
                description: t('notifications.markedBody', { count: unread.length }),
              });
            }}
          >
            {t('shell.notifications.markAllRead')}
          </Button>
        </header>

        <div className="border-b border-line-subtle px-4 py-4">
          <span className={cn(LABEL, 'mb-1.5 block')}>{t('notifications.category')}</span>
          <SegmentedControl
            label={t('notifications.filterLabel')}
            options={[
              {
                value: 'all',
                label: t('notifications.filterAll', { count: notifications.length }),
              },
              ...CATEGORIES.map((value) => ({
                value,
                label: t('notifications.filterCategory', {
                  category: t(CATEGORY_LABEL[value]),
                  count: notifications.filter((entry) => entry.category === value).length,
                }),
              })),
            ]}
            value={category}
            onValueChange={setCategory}
            size="sm"
          />
        </div>

        {visible.length === 0 ? (
          <div className="p-4">
            <EmptyState
              variant="table"
              title={t('notifications.emptyTitle')}
              description={t('notifications.emptyBody')}
              filtersActive={category !== 'all'}
              onClearFilters={() => setCategory('all')}
            />
          </div>
        ) : (
          <ul className="flex list-none flex-col p-0" aria-label={t('notifications.history')}>
            {visible.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-start gap-3 border-b border-line-subtle px-4 py-3 last:border-b-0 hover:bg-surface-alt"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-1.5 block size-2 shrink-0 rounded-pill',
                    CATEGORY_DOT[entry.category],
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block text-sm',
                      isRead(entry) ? 'text-body' : 'font-medium text-heading',
                    )}
                  >
                    {entry.title}
                    {isRead(entry) ? null : (
                      <span className="dhd-sr-only"> {t('notifications.unreadSuffix')}</span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">{entry.description}</span>
                  <span className="mt-1 block text-2xs text-muted">
                    {formatDateTime(entry.timestamp)}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Badge tone={CATEGORY_TONE[entry.category]} variant="subtle" size="sm">
                    {t(CATEGORY_LABEL[entry.category])}
                  </Badge>
                  {isRead(entry) ? (
                    <Badge tone="neutral" variant="subtle" size="sm">
                      {t('notifications.read')}
                    </Badge>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => markRead(entry)}>
                      {t('notifications.markAsRead')}
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title={t('notifications.reachedTitle')}
          description={t('notifications.reachedDescription')}
        >
          <ul className="flex list-none flex-col gap-4 p-0">
            {[...milestones]
              .sort((a, b) => b.date.localeCompare(a.date))
              .map((milestone) => (
                <li key={milestone.id} className="flex flex-wrap items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-sm text-body">{milestone.title}</span>
                    <span className="block text-2xs text-muted">
                      {findProject(milestone.projectId)?.name} ·{' '}
                      {vocabulary.phase(milestone.phaseId)}{' '}
                      · {formatDate(milestone.date)}
                    </span>
                  </span>
                  {milestone.status === 'upcoming' ? (
                    <Badge tone="info" variant="subtle" size="sm">
                      {t('notifications.ahead')}
                    </Badge>
                  ) : milestone.notified ? (
                    <Badge tone="success" variant="subtle" size="sm">
                      {t('notifications.notified')}
                    </Badge>
                  ) : (
                    <Badge tone="neutral" variant="subtle" size="sm">
                      {t('notifications.noNotification')}
                    </Badge>
                  )}
                </li>
              ))}
          </ul>

          <p className="mt-4 text-2xs text-muted">
            {t('notifications.summary', {
              count: notified.length,
              quiet: reachedQuietly.length,
            })}
          </p>
        </Panel>

        <Panel
          title={t('notifications.preferencesTitle')}
          description={t('notifications.preferencesDescription')}
        >
          <InlineAlert
            tone="info"
            title={t('notifications.selectiveTitle')}
            description={t('notifications.selectiveBody')}
          />

          <div className="mt-5">
            <RadioGroup
              name="notification-channel"
              label={t('notifications.channelLabel')}
              options={channelOptions}
              value={channel}
              onValueChange={(value) => {
                setChannel(value);
                toast({
                  variant: 'success',
                  title: t('notifications.channelSavedTitle'),
                  description: t('notifications.channelSavedBody', {
                    channel: channelPhrase(value),
                  }),
                });
              }}
            />
          </div>

          <div className="mt-6 border-t border-line-subtle pt-5">
            <span className={cn(LABEL, 'block')}>{t('notifications.eventsLabel')}</span>
            <div className="mt-3 flex flex-col gap-4">
              {EVENTS.map((event) => (
                <Toggle
                  key={event.key}
                  label={t(event.labelKey, { threshold })}
                  hint={t(event.hintKey)}
                  checked={events[event.key]}
                  onCheckedChange={(checked) => {
                    setEvents((current) => ({ ...current, [event.key]: checked }));
                    toast({
                      variant: 'info',
                      title: checked
                        ? t('notifications.eventEnabled')
                        : t('notifications.eventSilenced'),
                      description: checked
                        ? t('notifications.eventOnBody', {
                            event: t(event.labelKey, { threshold }),
                          })
                        : t('notifications.eventOffBody', {
                            event: t(event.labelKey, { threshold }),
                          }),
                    });
                  }}
                />
              ))}
            </div>
          </div>

          <p className="mt-4 text-2xs text-muted">
            {t('notifications.eventsSummary', {
              active: EVENTS.filter((event) => events[event.key]).length,
              total: EVENTS.length,
              channel: channelPhrase(channel),
            })}
          </p>
        </Panel>
      </div>
    </div>
  );
}
